import {
  AdvancedEditCut,
  TranscriptSegment,
} from '@app/routes/content/routes/recordings/domain';

import { FlatWord } from './build-flat-words';

// ── Types ────────────────────────────────────────────────────────────────────

export type EffectiveWord = {
  /** Display text — corrected word if a correction exists, otherwise original. */
  text: string;
  /** Original recording time (ms). */
  startMs: number;
  endMs: number;
  /** Remapped position on the edited-video timeline (ms). */
  editedStartMs: number;
  editedEndMs: number;
};

/**
 * A group of consecutive non-cut words that belong to the same original
 * segment and have no cut gap between them.
 * Each group maps to one VTT cue and one line of plain text.
 */
export type EffectiveWordGroup = {
  segmentIndex: number;
  speaker?: string;
  words: EffectiveWord[];
};

// ── Time remapping ───────────────────────────────────────────────────────────

/**
 * Remaps an original recording timestamp (ms) to the corresponding position
 * on the edited-video timeline.
 *
 * The edited video is the concatenation of all included cut regions in order.
 * Each cut removes a gap from the source timeline, shifting subsequent words
 * left by the cumulative duration of all gaps before them.
 *
 * Example:
 *   cuts = [{ 0→20 000 ms }, { 30 000→100 000 ms }]
 *   word originally at 35 000 ms (in cut 2):
 *     editedStart = 20 000 + (35 000 − 30 000) = 25 000 ms
 */
export const remapToEditedTime = (
  originalMs: number,
  sortedCuts: AdvancedEditCut[],
): number => {
  let cumulativeEditedStart = 0;

  for (const cut of sortedCuts) {
    const cutDuration = cut.end_time_ms - cut.start_time_ms;

    if (originalMs < cut.end_time_ms) {
      // Word falls within this cut. end_time_ms is exclusive — a word at
      // exactly end_time_ms belongs to the next region (gap or next cut).
      const offsetWithinCut = Math.max(0, originalMs - cut.start_time_ms);
      return cumulativeEditedStart + offsetWithinCut;
    }

    cumulativeEditedStart += cutDuration;
  }

  // Fallback: after all cuts — shouldn't happen for non-cut words
  return originalMs;
};

// ── Group building ───────────────────────────────────────────────────────────

/**
 * Builds the effective word groups from the full flat-word list.
 *
 * Rules:
 *  - A cut word closes the current group (splits the segment in two).
 *  - A word from a different segment starts a new group.
 *  - Consecutive non-cut words in the same segment form one group.
 *
 * Each group becomes one VTT cue / one text line.
 */
export const buildEffectiveGroups = (
  flatWords: FlatWord[],
  segments: TranscriptSegment[],
  cuts: AdvancedEditCut[],
): EffectiveWordGroup[] => {
  const sortedCuts = [...cuts].sort(
    (a, b) => a.start_time_ms - b.start_time_ms,
  );
  const groups: EffectiveWordGroup[] = [];
  let currentGroup: EffectiveWordGroup | null = null;

  const pushCurrentGroup = () => {
    if (currentGroup && currentGroup.words.length > 0) {
      groups.push(currentGroup);
    }
    currentGroup = null;
  };

  for (const flatWord of flatWords) {
    if (flatWord.isCut) {
      // Cut word — close the current group; a cut inside a segment creates two groups
      pushCurrentGroup();
      continue;
    }

    // Non-cut word: start a new group if segment changed or no current group
    if (!currentGroup || flatWord.segmentIndex !== currentGroup.segmentIndex) {
      pushCurrentGroup();
      currentGroup = {
        segmentIndex: flatWord.segmentIndex,
        speaker: segments[flatWord.segmentIndex]?.speaker,
        words: [],
      };
    }

    const startMs = flatWord.word.start * 1000;
    const endMs = flatWord.word.end * 1000;

    currentGroup.words.push({
      text: flatWord.correction ?? flatWord.word.word,
      startMs,
      endMs,
      editedStartMs: remapToEditedTime(startMs, sortedCuts),
      editedEndMs: remapToEditedTime(endMs, sortedCuts),
    });
  }

  pushCurrentGroup();
  return groups;
};

// ── VTT generation ───────────────────────────────────────────────────────────

/** Formats milliseconds as a VTT timestamp: `HH:MM:SS.mmm`. */
const formatVttTime = (ms: number): string => {
  const safeMs = Math.max(0, Math.round(ms));
  const totalSeconds = Math.floor(safeMs / 1000);
  const milliseconds = safeMs % 1000;
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);

  return (
    [
      String(hours).padStart(2, '0'),
      String(minutes).padStart(2, '0'),
      String(seconds).padStart(2, '0'),
    ].join(':') +
    '.' +
    String(milliseconds).padStart(3, '0')
  );
};

/**
 * Generates a WebVTT string from the effective word groups.
 *
 * Each group becomes one cue:
 *   - Timestamps are remapped edit-timeline times (not original recording times)
 *   - Text is the space-joined corrected words of the group
 */
export const buildEffectiveVtt = (groups: EffectiveWordGroup[]): string => {
  const cues = groups
    .filter(g => g.words.length > 0)
    .map(group => {
      const start = formatVttTime(group.words[0].editedStartMs);
      const end = formatVttTime(
        group.words[group.words.length - 1].editedEndMs,
      );
      const text = group.words.map(w => w.text).join(' ');
      return `${start} --> ${end}\n${text}`;
    })
    .join('\n\n');

  return `WEBVTT\n\n${cues}`;
};

// ── Plain text generation ────────────────────────────────────────────────────

/**
 * Generates plain text from the effective word groups.
 * Each group (segment or sub-segment after a cut) becomes one line.
 * No timestamps. Used for both the Copy button and the TXT download.
 */
export const buildEffectiveText = (groups: EffectiveWordGroup[]): string => {
  return groups
    .filter(g => g.words.length > 0)
    .map(group => group.words.map(w => w.text).join(' '))
    .join('\n');
};

// ── Browser download trigger ─────────────────────────────────────────────────

/** Creates a blob and triggers a browser file download. */
export const triggerFileDownload = (
  content: string,
  filename: string,
  mimeType: string,
): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};
